import { useState, useEffect, useLayoutEffect, useRef } from 'react';

// Height (px) of the Layout desktop title bar (<main>'s `sticky top-0` header,
// `hidden lg:flex`). It only exists at the lg breakpoint; below lg the page's
// scroll area has no inner sticky bar above it, so the page toolbar pins at 0.
// Matches Layout.js: px-4 py-2 row whose tallest control (NotificationBell) is
// 40px → 40 + 16 padding = 56px. Kept here as the single source for the offset.
export const APP_BAR_LG_PX = 56;

// Tailwind's `lg` breakpoint.
const LG_QUERY = '(min-width: 1024px)';

/**
 * Sticky page-toolbar helper (Planning pages: Breakdown / Manage Teams /
 * My Tasks). Returns:
 *   - toolbarRef:   attach to the toolbar element (measured for its height)
 *   - toolbarTop:   px the toolbar should pin at (app bar on lg, else 0)
 *   - headerTop:    px a SECOND-level sticky header (group/section header)
 *                   should pin at so it sits just beneath the pinned toolbar,
 *                   i.e. appBar + measured toolbar height. Robust to the
 *                   toolbar wrapping onto extra rows on narrow screens.
 *
 * The toolbar itself is pinned with static classes (`top-0 lg:top-14`) so it
 * never depends on JS to paint; only the header's larger offset needs the
 * measured value (an inline `top`).
 */
export default function useStickyToolbar() {
  const toolbarRef = useRef(null);
  const [toolbarH, setToolbarH] = useState(0);
  const [isLg, setIsLg] = useState(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(LG_QUERY).matches
      : true
  );

  // Track the lg breakpoint so the app-bar offset matches the CSS `lg:` gate.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(LG_QUERY);
    const onChange = () => setIsLg(mq.matches);
    onChange();
    // addEventListener('change') is the modern API; older Safari used addListener.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // 🚨 THE TOOLBAR MOUNTS LATE ON EVERY CALLER, so the element is tracked in
  // STATE (the `useDragScroll` fix, 2026-09-22). Every page using this hook
  // early-returns a spinner while `loading`, so on the first layout effect
  // `toolbarRef.current` is null — and until 2026-09-26 the measuring effect had
  // `[]` deps, never ran again, and `headerTop` stayed at the APP BAR ALONE (56)
  // on every page. Latent while the order tables' cap used their own measured
  // top; exposed the day `headerTop` became where their header PANE pins —
  // measured in Chromium: the header sat at 56px, underneath the 101px toolbar.
  // (My Tasks' section headers read `headerTop` directly and had the same bug.)
  const [toolbarEl, setToolbarEl] = useState(null);
  useLayoutEffect(() => {
    if (toolbarRef.current !== toolbarEl) setToolbarEl(toolbarRef.current);
  });

  // Measure the toolbar height before paint (useLayoutEffect avoids a one-frame
  // flash where the header would pin under the toolbar). A ResizeObserver keeps
  // it correct as the toolbar wraps/unwraps with viewport width.
  useLayoutEffect(() => {
    const el = toolbarEl;
    if (!el) return undefined;
    const measure = () => setToolbarH(Math.round(el.getBoundingClientRect().height));
    measure();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [toolbarEl]);

  const appBar = isLg ? APP_BAR_LG_PX : 0;
  return { toolbarRef, toolbarTop: appBar, headerTop: appBar + toolbarH };
}
